import { useAppStore } from '@/store';
import { cn } from '@/lib/utils';
import {
  BrainCircuit, FileText, Users, Clock, File, Upload, Download,
  AlertTriangle, CheckCircle2, Check, X, Pencil, Trash2,
  ListChecks, Play, Loader2, Sparkles, Star, StarOff, MessageSquare,
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { SIMPLE } from '@/lib/config';

type Tab = 'context' | 'docs' | 'plan' | 'participants' | 'timeline';

const ALL_TABS: { id: Tab; label: string; icon: React.ElementType }[] = [
  { id: 'context', label: 'Контекст', icon: BrainCircuit },
  { id: 'plan', label: 'План', icon: ListChecks },
  { id: 'docs', label: 'Файли', icon: FileText },
  { id: 'participants', label: 'Особи', icon: Users },
  { id: 'timeline', label: 'Хронологія', icon: Clock },
];
const TABS = SIMPLE ? ALL_TABS.filter((t) => t.id === 'docs') : ALL_TABS;

const NARRATIVE_KEYS = [
  'situation_description', 'violations', 'evidence', 'arguments',
  'desired_outcome', 'financial_losses', 'pre_trial_attempts',
];
const PARTY_KEYS = [
  'plaintiff_name', 'plaintiff_address', 'plaintiff_phone', 'plaintiff_email',
  'plaintiff_birth_date', 'plaintiff_tax_id', 'plaintiff_passport',
  'defendant_name', 'defendant_address', 'defendant_phone', 'defendant_email',
  'defendant_tax_id', 'defendant_edrpou',
  'court_name', 'court_address',
];

// Fragment-level Ukrainian translations for any field key the server didn't
// label explicitly (e.g. when a template introduces a new key).
const FRAGMENT_UA: Record<string, string> = {
  plaintiff: 'позивача', defendant: 'відповідача', court: 'суду',
  contract: 'договору', property: 'обʼєкту', case: 'справи',
  name: 'найменування', address: 'адреса', phone: 'телефон',
  email: 'email', date: 'дата', amount: 'сума', subject: 'предмет',
  description: 'опис', number: 'номер', id: 'ID', tax: 'податковий',
  passport: 'паспорт', edrpou: 'ЄДРПОУ', actions: 'дії',
  title: 'назва', deadline: 'термін', fee: 'збір', filing: 'подання',
  incident: 'подія', losses: 'збитки', financial: 'фінансові',
  desired: 'бажаний', outcome: 'результат', evidence: 'докази',
  arguments: 'аргументи', violations: 'порушення', situation: 'ситуація',
  pre: 'до', trial: 'суду', attempts: 'спроби', narrative: 'опис',
  birth: 'народження', requested: 'запитувані', claimed: 'заявлена',
  statement: 'заява',
};

function smartFallback(key: string): string {
  if (!key) return '';
  const parts = key.split('_').map((p) => FRAGMENT_UA[p.toLowerCase()] || p);
  const text = parts.join(' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function useLabelFor() {
  const fieldLabels = useAppStore((s) => s.fieldLabels);
  return (key: string) => fieldLabels[key] || smartFallback(key);
}

function EditableField({
  fieldKey,
  value,
  onSave,
  onDelete,
}: {
  fieldKey: string;
  value: string;
  onSave: (next: string) => Promise<void> | void;
  onDelete: () => Promise<void> | void;
}) {
  const labelFor = useLabelFor();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [busy, setBusy] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing && taRef.current) {
      taRef.current.focus();
      taRef.current.setSelectionRange(taRef.current.value.length, taRef.current.value.length);
      taRef.current.style.height = 'auto';
      taRef.current.style.height = Math.min(taRef.current.scrollHeight, 280) + 'px';
    }
  }, [editing]);

  const commit = async () => {
    if (draft === value) {
      setEditing(false);
      return;
    }
    setBusy(true);
    try {
      await onSave(draft);
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  return (
    <div
      className={cn(
        'group bg-white/5 border rounded-xl p-3 transition-colors',
        editing ? 'border-blue-500/50 bg-white/[0.07]' : 'border-white/10 hover:border-white/20'
      )}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[10px] text-white/30 uppercase tracking-wider">{labelFor(fieldKey)}</div>
        {!editing && (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => setEditing(true)}
              className="p-1 text-white/40 hover:text-white"
              title="Редагувати"
            >
              <Pencil className="h-3 w-3" />
            </button>
            <button
              onClick={onDelete}
              className="p-1 text-white/40 hover:text-red-400"
              title="Видалити"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>

      {editing ? (
        <div className="space-y-2">
          <textarea
            ref={taRef}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              const t = e.target as HTMLTextAreaElement;
              t.style.height = 'auto';
              t.style.height = Math.min(t.scrollHeight, 280) + 'px';
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') cancel();
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) commit();
            }}
            disabled={busy}
            className="w-full bg-black/30 border border-white/10 rounded-lg p-2 text-xs text-white leading-relaxed resize-none focus:outline-none focus:border-blue-500/50 min-h-[60px] max-h-[280px]"
          />
          <div className="flex justify-end gap-1.5">
            <button
              onClick={cancel}
              disabled={busy}
              className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-[11px] text-white/60 transition-colors disabled:opacity-50"
            >
              <X className="h-3 w-3" /> Скасувати
            </button>
            <button
              onClick={commit}
              disabled={busy}
              className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-blue-600 hover:bg-blue-500 text-[11px] text-white transition-colors disabled:opacity-50"
            >
              <Check className="h-3 w-3" /> Зберегти
            </button>
          </div>
          <div className="text-[9px] text-white/20 text-right">⌘+Enter — зберегти, Esc — скасувати</div>
        </div>
      ) : (
        <button
          onClick={() => setEditing(true)}
          className="w-full text-left text-xs text-white/80 leading-relaxed whitespace-pre-wrap break-words cursor-text"
        >
          {value || <span className="text-white/30 italic">порожньо — клік для редагування</span>}
        </button>
      )}
    </div>
  );
}

export function ContextPanel() {
  const activeCaseId = useAppStore((s) => s.activeCaseId);
  const activeSession = useAppStore((s) => s.activeSession);
  const uploadFiles = useAppStore((s) => s.uploadFiles);
  const updateField = useAppStore((s) => s.updateField);
  const seenFiles = useAppStore((s) => (activeCaseId ? s.seenFiles[activeCaseId] : undefined));
  const markFileSeen = useAppStore((s) => s.markFileSeen);
  const setMobileContextOpen = useAppStore((s) => s.setMobileContextOpen);
  const openPreview = useAppStore((s) => s.openPreview);
  const previewLoading = useAppStore((s) => s.previewLoading);
  const previewDocumentId = useAppStore((s) => s.previewDocumentId);
  const toggleDocAsTemplate = useAppStore((s) => s.toggleDocAsTemplate);
  const deleteUploadedDoc = useAppStore((s) => s.deleteUploadedDoc);
  const sendUserMessage = useAppStore((s) => s.sendUserMessage);
  const [activeTab, setActiveTab] = useState<Tab>(SIMPLE ? 'docs' : 'context');
  const requestedContextTab = useAppStore((s) => s.requestedContextTab);
  const requestContextTab = useAppStore((s) => s.requestContextTab);
  const setMobileContextOpenRef = useAppStore((s) => s.setMobileContextOpen);

  useEffect(() => {
    if (!requestedContextTab) return;
    const allowed = TABS.some((t) => t.id === requestedContextTab);
    if (allowed) {
      setActiveTab(requestedContextTab);
      setMobileContextOpenRef(true);
    }
    requestContextTab(null);
  }, [requestedContextTab, requestContextTab, setMobileContextOpenRef]);

  if (!activeCaseId) return null;

  const data = activeSession?.collectedData ?? {};
  const docs = activeSession?.uploadedDocs ?? [];
  const genFiles = activeSession?.generatedFiles ?? [];
  const plan = activeSession?.documentPlan ?? [];
  const pendingPlanCount = plan.filter((p) => p.status === 'pending').length;

  const seenSet = new Set(seenFiles ?? []);
  const unreadFiles = genFiles.filter((f) => {
    const url = typeof f === 'string' ? f : f.url;
    return !seenSet.has(url);
  });

  const allKeys = Object.keys(data);
  const narrativeFields = NARRATIVE_KEYS.filter((k) => k in data);
  const partyFields = PARTY_KEYS.filter((k) => k in data);
  const otherFields = allKeys
    .filter((k) => !NARRATIVE_KEYS.includes(k) && !PARTY_KEYS.includes(k));

  const participants = [
    data.plaintiff_name && { role: 'Позивач', name: data.plaintiff_name },
    data.defendant_name && { role: 'Відповідач', name: data.defendant_name },
    data.court_name && { role: 'Суд', name: data.court_name },
  ].filter(Boolean) as { role: string; name: string }[];

  const handleUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.accept = 'image/*,.pdf,.docx,.doc,.md,.markdown,.txt,.csv,.html,.htm,.json,.xml';
    input.onchange = (e) => {
      const files = Array.from((e.target as HTMLInputElement).files || []);
      if (files.length > 0) uploadFiles(files);
    };
    input.click();
  };

  return (
    <aside className="flex-1 flex flex-col border-l border-white/10 bg-[var(--t-panel)] min-w-0">
      {/* Mobile close button — visible only when this panel acts as a drawer (xl:hidden) */}
      <div className="xl:hidden flex items-center justify-between px-4 py-3 border-b border-white/5">
        <span className="text-xs font-semibold uppercase tracking-widest text-white/50">Контекст справи</span>
        <button
          onClick={() => setMobileContextOpen(false)}
          className="p-1.5 rounded-md text-white/40 hover:text-white hover:bg-white/5 transition-colors"
          title="Закрити"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {/* Tab bar */}
      <div className="flex border-b border-white/10">
        {TABS.map((tab) => {
          const badge =
            tab.id === 'docs' ? unreadFiles.length :
            tab.id === 'plan' ? pendingPlanCount : 0;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              title={tab.label}
              className={cn(
                'relative flex-1 py-3 flex flex-col items-center gap-0.5 text-[9px] font-bold uppercase tracking-widest transition-colors border-b-2',
                activeTab === tab.id
                  ? 'border-blue-600 text-white'
                  : 'border-transparent text-white/30 hover:text-white/60'
              )}
            >
              <tab.icon className="h-4 w-4" />
              {badge > 0 && (
                <span className="absolute top-1.5 right-1/2 translate-x-3 min-w-[16px] h-[16px] px-1 rounded-full bg-emerald-500 text-[9px] font-bold text-white flex items-center justify-center leading-none ring-2 ring-[var(--t-panel)]">
                  {badge > 9 ? '9+' : badge}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-white/10">
        {/* CONTEXT TAB */}
        {activeTab === 'context' && (
          <>
            {/* Template required fields checklist */}
            {(() => {
              const templateFields = activeSession?.templateFields ?? [];
              if (templateFields.length === 0) return null;
              const pendingFields = templateFields.filter((f) => {
                const v = String(data[f.key] ?? '').trim();
                return v.length === 0;
              });
              const doneCount = templateFields.length - pendingFields.length;
              return (
                <div className="space-y-1.5">
                  <h4 className="text-[10px] font-bold text-white/40 uppercase tracking-widest flex items-center justify-between">
                    <span>Потрібно заповнити</span>
                    <span className={cn(
                      'text-[10px] normal-case tracking-normal font-normal',
                      doneCount === templateFields.length ? 'text-emerald-400' : 'text-white/30'
                    )}>
                      {doneCount}/{templateFields.length}
                    </span>
                  </h4>
                  {templateFields.map((f) => {
                    const filled = String(data[f.key] ?? '').trim().length > 0;
                    return (
                      <div key={f.key} className={cn(
                        'flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs transition-colors',
                        filled ? 'text-emerald-400/80' : 'text-white/40'
                      )}>
                        <div className={cn(
                          'w-3.5 h-3.5 rounded-full border flex-shrink-0 flex items-center justify-center',
                          filled ? 'border-emerald-500 bg-emerald-500/20' : 'border-white/20'
                        )}>
                          {filled && <Check className="w-2 h-2 text-emerald-400" />}
                        </div>
                        <span className={filled ? 'line-through opacity-60' : ''}>
                          {f.label || f.key}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {allKeys.length === 0 ? (
              <div className="text-center py-8 text-white/20 text-xs">
                Дані з'являться в процесі діалогу
              </div>
            ) : (
              <div className="space-y-5">
                {narrativeFields.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-[10px] font-bold text-white/40 uppercase tracking-widest">
                      Історія справи
                    </h4>
                    {narrativeFields.map((k) => (
                      <EditableField
                        key={k}
                        fieldKey={k}
                        value={String(data[k] ?? '')}
                        onSave={(v) => updateField(k, v)}
                        onDelete={() => updateField(k, null)}
                      />
                    ))}
                  </div>
                )}

                {partyFields.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-[10px] font-bold text-white/40 uppercase tracking-widest">
                      Сторони
                    </h4>
                    {partyFields.map((k) => (
                      <EditableField
                        key={k}
                        fieldKey={k}
                        value={String(data[k] ?? '')}
                        onSave={(v) => updateField(k, v)}
                        onDelete={() => updateField(k, null)}
                      />
                    ))}
                  </div>
                )}

                {otherFields.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-[10px] font-bold text-white/40 uppercase tracking-widest">
                      Додаткові реквізити
                    </h4>
                    {otherFields.map((k) => (
                      <EditableField
                        key={k}
                        fieldKey={k}
                        value={String(data[k] ?? '')}
                        onSave={(v) => updateField(k, v)}
                        onDelete={() => updateField(k, null)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* State indicator */}
            {activeSession?.state && (
              <div
                className={cn(
                  'flex items-center gap-2 p-3 rounded-xl border text-xs',
                  activeSession.state === 'DELIVERY'
                    ? 'bg-emerald-900/20 border-emerald-500/20 text-emerald-400'
                    : activeSession.state === 'GENERATION'
                    ? 'bg-blue-900/20 border-blue-500/20 text-blue-400'
                    : 'bg-white/5 border-white/10 text-white/40'
                )}
              >
                {activeSession.state === 'DELIVERY' ? (
                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                ) : (
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                )}
                <span>Статус: <strong>{activeSession.state}</strong></span>
              </div>
            )}
          </>
        )}

        {/* PLAN TAB */}
        {activeTab === 'plan' && (
          <div className="space-y-3">
            <div className="text-[10px] font-bold text-white/40 uppercase tracking-widest flex items-center justify-between">
              <span>Документи до підготовки</span>
              <span className="text-white/30 normal-case tracking-normal">{plan.length}</span>
            </div>
            {plan.length === 0 ? (
              <div className="text-center py-10 text-white/20 text-xs">
                Список зʼявиться, коли бот зрозуміє предмет справи.
              </div>
            ) : (
              <div className="space-y-2">
                {plan.map((item, i) => {
                  const isGenerated = item.status === 'generated';
                  const isCurrent = previewDocumentId === item.id && previewLoading;
                  return (
                    <div
                      key={item.id}
                      className={cn(
                        'rounded-xl border p-3 transition-colors',
                        isGenerated
                          ? 'bg-emerald-900/15 border-emerald-500/25'
                          : 'bg-white/5 border-white/10 hover:border-white/20'
                      )}
                    >
                      <div className="flex items-start gap-2">
                        <div className={cn(
                          'w-6 h-6 rounded-full shrink-0 flex items-center justify-center text-[10px] font-bold',
                          isGenerated ? 'bg-emerald-500/30 text-emerald-300' : 'bg-white/10 text-white/60'
                        )}>
                          {isGenerated ? <Check className="w-3.5 h-3.5" /> : (i + 1)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <span className={cn(
                              'text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded',
                              item.type === 'main'
                                ? 'bg-blue-500/20 text-blue-300'
                                : 'bg-white/10 text-white/50'
                            )}>
                              {item.type === 'main' ? 'Основний' : item.type === 'reminder' ? 'Нагадування' : 'Додаток'}
                            </span>
                          </div>
                          <div className="text-xs font-medium text-white leading-snug">{item.title}</div>
                          {item.description && (
                            <div className="text-[11px] text-white/50 leading-relaxed mt-1">{item.description}</div>
                          )}
                          {isGenerated && item.file_url && (
                            <a
                              href={item.file_url}
                              download={item.filename || 'document.docx'}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 text-[11px] text-emerald-300 hover:text-emerald-200 mt-2"
                            >
                              <Download className="w-3 h-3" />
                              {item.filename || 'Завантажити'}
                            </a>
                          )}
                          {!isGenerated && item.type !== 'reminder' && (
                            <button
                              onClick={() => openPreview(item.id)}
                              disabled={previewLoading}
                              className={cn(
                                'mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors',
                                isCurrent
                                  ? 'bg-blue-600/30 text-blue-200 border border-blue-500/40'
                                  : 'bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50'
                              )}
                            >
                              {isCurrent
                                ? <><Loader2 className="w-3 h-3 animate-spin" /> Готую…</>
                                : <><Play className="w-3 h-3" /> Згенерувати</>}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {pendingPlanCount > 0 && (
                  <button
                    onClick={() => openPreview()}
                    disabled={previewLoading}
                    className="w-full mt-3 flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold transition-colors disabled:opacity-50"
                  >
                    <Sparkles className="w-4 h-4" />
                    Згенерувати все ({pendingPlanCount})
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* DOCS TAB */}
        {activeTab === 'docs' && (
          <>
            <button
              onClick={handleUpload}
              className="w-full py-2 px-3 bg-white/5 border border-dashed border-white/20 rounded-xl text-xs text-white/50 hover:bg-white/10 hover:text-white transition-colors flex items-center justify-center gap-2"
            >
              <Upload className="h-3.5 w-3.5" /> Завантажити документ
            </button>

            {docs.length === 0 && genFiles.length === 0 ? (
              <div className="text-center py-8 text-white/20 text-xs">Файлів поки немає</div>
            ) : (
              <div className="space-y-2">
                {docs.map((doc, i) => {
                  const d = doc as any;
                  const isOriginal = !!d.useAsTemplate;
                  const canMark = !!d.id;
                  return (
                    <div
                      key={i}
                      className={cn(
                        'relative flex items-start gap-3 p-3 pr-9 rounded-xl border',
                        isOriginal
                          ? 'bg-amber-900/20 border-amber-500/40'
                          : 'bg-white/5 border-white/10'
                      )}
                    >
                      {canMark && (
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm(`Видалити "${d.filename}"?`)) deleteUploadedDoc(d.id);
                          }}
                          title="Видалити документ"
                          aria-label="Видалити документ"
                          className="absolute top-2 right-2 p-1 rounded-md text-white/40 hover:text-red-300 hover:bg-red-500/10 transition-colors"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <div className={cn(
                        'w-8 h-8 rounded-lg flex items-center justify-center shrink-0',
                        isOriginal ? 'bg-amber-900/40' : 'bg-black/30'
                      )}>
                        <File className={cn('h-4 w-4', isOriginal ? 'text-amber-300' : 'text-blue-400')} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-white truncate" title={d.filename}>
                          {d.filename}
                        </div>
                        {d.summary && (
                          <div className="text-[11px] text-white/60 leading-snug mt-0.5">
                            {d.summary}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-[10px] text-white/30 uppercase">
                            {d.kind || d.status || 'uploaded'}
                          </span>
                          {isOriginal && (
                            <span className="text-[10px] font-semibold text-amber-300 uppercase tracking-wider">
                              · основа документа
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 flex-wrap mt-2">
                          {canMark && (
                            <button
                              onClick={() => toggleDocAsTemplate(d.id)}
                              title={isOriginal
                                ? 'Зняти позначку — генерувати з шаблону'
                                : 'Позначити цей файл як мій оригінал — система буде дописувати його, а не генерувати з нуля'}
                              className={cn(
                                'inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider px-2 py-1 rounded-md transition-colors',
                                isOriginal
                                  ? 'bg-amber-500/20 text-amber-200 hover:bg-amber-500/30'
                                  : 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white'
                              )}
                            >
                              {isOriginal ? <StarOff className="h-3 w-3" /> : <Star className="h-3 w-3" />}
                              {isOriginal ? 'Зняти основу' : 'Це мій оригінал'}
                            </button>
                          )}
                          <button
                            onClick={() => sendUserMessage(`Зверни увагу на документ «${d.filename}» і використай його у своїй роботі.`)}
                            title="Вказати ІІ на цей документ — надіслати повідомлення в чат"
                            className="inline-flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider px-2 py-1 rounded-md bg-blue-500/10 text-blue-300/70 hover:bg-blue-500/20 hover:text-blue-200 transition-colors"
                          >
                            <MessageSquare className="h-3 w-3" />
                            Вказати ІІ
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {genFiles.length > 0 && (
                  <>
                    <div className="text-[10px] font-bold text-emerald-400/60 uppercase tracking-widest pt-2 flex items-center justify-between">
                      <span>Згенеровані</span>
                      <span className="text-emerald-400/40 normal-case tracking-normal">{genFiles.length}</span>
                    </div>
                    {[...genFiles].reverse().map((f, i) => {
                      const url = typeof f === 'string' ? f : f.url;
                      const name = typeof f === 'string'
                        ? (f.split('/').pop() || 'document.docx')
                        : f.filename;
                      const ts = typeof f === 'string' ? null : f.generatedAt;
                      const isNew = !seenSet.has(url);
                      return (
                        <a
                          key={i}
                          href={url}
                          download={name}
                          target="_blank"
                          rel="noreferrer"
                          title={name}
                          onClick={() => markFileSeen(activeCaseId, url)}
                          onAuxClick={() => markFileSeen(activeCaseId, url)}
                          className={cn(
                            'flex items-center gap-3 p-3 rounded-xl border transition-colors relative',
                            isNew
                              ? 'bg-emerald-900/30 border-emerald-500/40 hover:bg-emerald-900/40'
                              : 'bg-white/5 border-white/10 hover:bg-white/10'
                          )}
                        >
                          {isNew && (
                            <span
                              className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-emerald-400 ring-2 ring-[var(--t-panel)]"
                              title="Новий"
                            />
                          )}
                          <div className={cn(
                            'w-8 h-8 rounded-lg flex items-center justify-center shrink-0',
                            isNew ? 'bg-emerald-900/50' : 'bg-white/5'
                          )}>
                            <Download className={cn('h-4 w-4', isNew ? 'text-emerald-400' : 'text-white/50')} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className={cn(
                              'text-xs font-medium truncate',
                              isNew ? 'text-emerald-300' : 'text-white/70'
                            )} title={name}>
                              {name}
                            </div>
                            <div className={cn(
                              'text-[10px]',
                              isNew ? 'text-emerald-400/50' : 'text-white/30'
                            )}>
                              {ts
                                ? new Date(ts).toLocaleString('uk-UA', {
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                  })
                                : 'Завантажити'}
                              {!isNew && ' · скачано'}
                            </div>
                          </div>
                        </a>
                      );
                    })}
                  </>
                )}
              </div>
            )}
          </>
        )}

        {/* PARTICIPANTS TAB */}
        {activeTab === 'participants' && (
          <>
            {participants.length === 0 ? (
              <div className="text-center py-12 text-white/20 text-xs">
                Учасники з'являться після збору даних
              </div>
            ) : (
              <div className="space-y-2">
                {participants.map((p, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10"
                  >
                    <div>
                      <div className="text-xs font-medium text-white">{p.name}</div>
                      <div className="text-[10px] text-white/30 uppercase mt-0.5">{p.role}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* TIMELINE TAB */}
        {activeTab === 'timeline' && (
          <>
            {!data.incident_date && !data.deadline ? (
              <div className="text-center py-12 text-white/20 text-xs">
                Дати з'являться після збору даних
              </div>
            ) : (
              <div className="relative pl-6 space-y-4">
                <div className="absolute left-2 top-1 bottom-1 w-px bg-white/10" />
                {data.incident_date && (
                  <div className="relative">
                    <div className="absolute -left-4 top-1.5 w-2.5 h-2.5 rounded-full bg-blue-600 border-2 border-[var(--t-panel)]" />
                    <div className="text-[10px] text-white/30 uppercase mb-1">Подія</div>
                    <div className="text-xs text-white/70 p-2 rounded-lg bg-white/5 border border-white/10">
                      {String(data.incident_date)}
                    </div>
                  </div>
                )}
                {data.deadline && (
                  <div className="relative">
                    <div className="absolute -left-4 top-1.5 w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-[var(--t-panel)]" />
                    <div className="text-[10px] text-red-400/70 uppercase mb-1">Дедлайн</div>
                    <div className="text-xs text-white/70 p-2 rounded-lg bg-red-900/20 border border-red-500/20">
                      {String(data.deadline)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </aside>
  );
}
